import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, Package, Loader2, Truck } from "lucide-react";
import { Button } from "@/app/components/button";
import { formatCurrency } from "@/config/locale";
import {
  getOrder,
  getPaymentIntent,
  getShippingOptions,
  getHubDisplay,
  getOfferById,
  getAdById,
  apiAdToAd,
  type OrderDetailApi,
  type ShippingOptionForCheckout,
  type HubDisplay,
} from "@/app/api/auth-client";
import { CheckoutShippingPage, type ShippingOption, type ShippingRouteLabels } from "@/app/components/checkout-shipping-page";
import { AdImage } from "@/app/components/common/ad-image";
import { getAdImage } from "@/app/utils/ad-utils";

function safeText(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return "";
}

/** One line item from order (BFF returns ad_id, product_name, dimensions, etc.). */
interface OrderLineItemApi {
  ad_id?: string;
  product_name?: string;
  quantity?: number;
  unit_price_cents?: number;
  line_total_cents?: number;
  offer_id?: string;
  width_cm?: number;
  height_cm?: number;
  length_cm?: number;
  weight_kg?: number;
}

interface CheckoutSecondPartyPageProps {
  orderId: string;
  onBack: () => void;
  onPaymentIntentReady: (orderId: string, clientSecret: string, publishableKey: string) => void;
}

type Step = "shipping" | "review";

/** Loaded from offer (product_ad_ids + amount_cents) for "Detalhes da Oferta" on review. */
interface OfferDetails {
  tradeInAds: Array<{ id: string; title: string; value: number; image: string }>;
  cashAmount: number;
  totalOfferValue: number;
}

/** Build hub params and items from order for POST /auth/me/checkout/shipping-options. */
function buildHubParamsFromOrder(order: OrderDetailApi): {
  cep: string;
  items: Array<{ ad_id: string; quantity: number }>;
  recipient_name: string;
  recipient_phone: string;
  hubParams?: import("@/app/api/auth-client").ShippingOptionsHubParams;
} {
  const cep = (order.address_snapshot?.cep ?? "").replace(/\D/g, "").slice(0, 8);
  const lineItems = (order.line_items ?? []) as OrderLineItemApi[];
  const firstLine = lineItems[0];
  const items =
    firstLine?.ad_id != null
      ? [{ ad_id: firstLine.ad_id, quantity: Math.max(1, firstLine.quantity ?? 1) }]
      : [];
  const recipient_name = order.address_snapshot?.recipient_name?.trim() ?? "";
  const recipient_phone = order.address_snapshot?.recipient_phone?.trim() ?? "";

  // Buyer's origin for leg 1.1 is the same as address_snapshot (buyer's delivery/send-from address).
  const buyerOriginCep = (order.address_snapshot?.cep ?? "").replace(/\D/g, "").slice(0, 8);
  const sellerDestCep = (order.origin_snapshot?.cep ?? "").replace(/\D/g, "").slice(0, 8);

  const hubParams: import("@/app/api/auth-client").ShippingOptionsHubParams | undefined =
    cep.length === 8 && buyerOriginCep.length === 8 && sellerDestCep.length === 8 && firstLine
      ? {
          use_hub_routing: true,
          buyer_origin_cep: buyerOriginCep,
          seller_destination_cep: sellerDestCep,
          buyer_products: [
            {
              id: firstLine.ad_id ?? "item",
              width: Math.max(1, firstLine.width_cm ?? 16),
              height: Math.max(1, firstLine.height_cm ?? 11),
              length: Math.max(1, firstLine.length_cm ?? 11),
              weight: Math.max(0.01, firstLine.weight_kg ?? 1),
              insurance_value: Math.max(0.01, (firstLine.line_total_cents ?? 0) / 100),
              quantity: Math.max(1, firstLine.quantity ?? 1),
            },
          ],
          seller_destination_recipient_name: order.origin_snapshot?.name?.trim(),
          seller_destination_recipient_phone: order.origin_snapshot?.phone?.trim(),
        }
      : undefined;

  return { cep, items, recipient_name, recipient_phone, hubParams };
}

/** Second party is seller when first payer was buyer. */
function isSecondPartySeller(order: OrderDetailApi): boolean {
  return (order.first_payer_party ?? "").trim().toLowerCase() === "buyer";
}

/**
 * Same trajeto card as buyer review: origem → hub → destino.
 * For swap + hub: always show when hub is known (fallbacks if snapshot cities are missing).
 */
function buildSecondPartyReviewRouteLabels(
  order: OrderDetailApi,
  hubDisplay: HubDisplay | null,
  isSeller: boolean
): ShippingRouteLabels | null {
  if (!hubDisplay || (order.deal_type ?? "").trim().toLowerCase() !== "swap") {
    return null;
  }
  const hubLabel = [hubDisplay.city, hubDisplay.state].filter(Boolean).join(" / ") || "Hub";
  const dest =
    [order.address_snapshot?.city, order.address_snapshot?.state].filter(Boolean).join(" / ") || "Destino";
  const origin = isSeller
    ? [order.origin_snapshot?.city, order.origin_snapshot?.state].filter(Boolean).join(" / ") || "Origem"
    : [order.address_snapshot?.city, order.address_snapshot?.state].filter(Boolean).join(" / ") || "Origem";
  return { origin, hub: hubLabel, destination: dest };
}

/** Per-leg amounts (reais) in the same slots as toSecondPartyOptions (Trecho visual = Até o hub / Hub até destino). */
function secondPartyReviewLegsReais(
  order: OrderDetailApi,
  opt: ShippingOption | null
): { leg1: number | null; leg2: number | null } {
  if (opt && (opt.leg1_1 != null || opt.leg2 != null)) {
    return { leg1: opt.leg1_1 ?? null, leg2: opt.leg2 ?? null };
  }
  const first = (order.first_payer_party ?? "").trim().toLowerCase();
  if (first === "buyer") {
    return {
      leg1: order.leg1_quoted_cents != null && order.leg1_quoted_cents > 0 ? order.leg1_quoted_cents / 100 : null,
      leg2: order.leg2_1_quoted_cents != null && order.leg2_1_quoted_cents > 0 ? order.leg2_1_quoted_cents / 100 : null,
    };
  }
  if (first === "seller") {
    return {
      leg1: order.leg1_1_quoted_cents != null && order.leg1_1_quoted_cents > 0 ? order.leg1_1_quoted_cents / 100 : null,
      leg2: order.leg2_quoted_cents != null && order.leg2_quoted_cents > 0 ? order.leg2_quoted_cents / 100 : null,
    };
  }
  return { leg1: null, leg2: null };
}

/** Map API options to UI options with "my part" as price (seller_amount or buyer_amount). For seller, show Leg 1 + Leg 2.1 in the same slots as buyer's Leg 1.1 + Leg 2 so route and card show per-leg cost/days. */
function toSecondPartyOptions(
  options: ShippingOptionForCheckout[],
  order: OrderDetailApi
): ShippingOption[] {
  const isSeller = isSecondPartySeller(order);
  return options.map((o) => {
    const myAmountReais = isSeller ? (o.sellerAmount ?? o.price) : (o.buyerAmount ?? o.price);
    const sellerTotalDays =
      isSeller && o.leg1EstimatedDays != null && o.leg2_1EstimatedDays != null
        ? o.leg1EstimatedDays + o.leg2_1EstimatedDays
        : null;
    const estimatedDays =
      sellerTotalDays != null && sellerTotalDays > 0
        ? sellerTotalDays === 1
          ? "1 dia útil"
          : `Até ${sellerTotalDays} dias úteis`
        : o.estimatedDays;
    return {
      id: o.id,
      name: o.name,
      description: o.description,
      estimatedDays,
      price: myAmountReais,
      type: o.type,
      buyerAmount: myAmountReais,
      sellerAmount: o.sellerAmount,
      leg1: o.leg1,
      leg2_1: o.leg2_1,
      leg1_1: isSeller ? o.leg1 : o.leg1_1,
      leg2: isSeller ? o.leg2_1 : o.leg2,
      leg1_1EstimatedDays: isSeller ? (o.leg1EstimatedDays ?? o.leg1_1EstimatedDays) : o.leg1_1EstimatedDays,
      leg2EstimatedDays: isSeller ? (o.leg2_1EstimatedDays ?? o.leg2EstimatedDays) : o.leg2EstimatedDays,
      leg1EstimatedDays: o.leg1EstimatedDays,
      leg2_1EstimatedDays: o.leg2_1EstimatedDays,
      rawPriceCents: o.rawPriceCents,
      freightMarkupCents: o.freightMarkupCents,
      buyerRawPriceCents: o.buyerRawPriceCents,
      buyerFreightMarkupCents: o.buyerFreightMarkupCents,
      sellerRawPriceCents: o.sellerRawPriceCents,
      sellerFreightMarkupCents: o.sellerFreightMarkupCents,
    };
  });
}

export function CheckoutSecondPartyPage({
  orderId,
  onBack,
  onPaymentIntentReady,
}: CheckoutSecondPartyPageProps) {
  const [order, setOrder] = useState<OrderDetailApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [step, setStep] = useState<Step>("shipping");
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  /** Start true so we never paint "sem opções" before the first quote request runs (effect runs after paint). */
  const [shippingOptionsLoading, setShippingOptionsLoading] = useState(true);
  const [shippingOptionsError, setShippingOptionsError] = useState<string | null>(null);
  const [selectedShippingId, setSelectedShippingId] = useState<string | null>(null);
  const [hubDisplay, setHubDisplay] = useState<HubDisplay | null>(null);
  const [offerDetails, setOfferDetails] = useState<OfferDetails | null>(null);
  const [offerDetailsLoading, setOfferDetailsLoading] = useState(false);
  const [orderProductAd, setOrderProductAd] = useState<Awaited<ReturnType<typeof apiAdToAd>> | null>(null);
  const [orderProductAdLoading, setOrderProductAdLoading] = useState(false);

  /** Avoid infinite effect loop when quote fails (options stay empty and loading becomes false). */
  const autoShippingQuoteOnceRef = useRef(false);

  useEffect(() => {
    if (step !== "shipping") autoShippingQuoteOnceRef.current = false;
  }, [step]);

  useEffect(() => {
    autoShippingQuoteOnceRef.current = false;
    setShippingOptionsLoading(true);
    setShippingOptions([]);
    setShippingOptionsError(null);
  }, [orderId]);

  // Hub para trajeto na revisão ou se a cotação falhou antes de preencher hub.
  useEffect(() => {
    if ((step !== "review" && step !== "shipping") || hubDisplay) return;
    let cancelled = false;
    getHubDisplay().then((h) => {
      if (!cancelled && h) setHubDisplay(h);
    });
    return () => {
      cancelled = true;
    };
  }, [step, hubDisplay]);

  useEffect(() => {
    let cancelled = false;
    getOrder(orderId)
      .then((data) => {
        if (!cancelled) setOrder(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? "Não foi possível carregar o pedido.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const loadShippingOptions = useCallback(() => {
    if (!order) return;
    const { cep, items, recipient_name, recipient_phone, hubParams } = buildHubParamsFromOrder(order);
    if (cep.length < 8 || items.length === 0) {
      setShippingOptionsError("Endereço ou itens do pedido incompletos.");
      setShippingOptionsLoading(false);
      return;
    }
    setShippingOptionsError(null);
    setShippingOptionsLoading(true);
    void (async () => {
      try {
        // Hub antes das cotações: mesmo trajeto visual do comprador na etapa Envio.
        const hub = await getHubDisplay().catch(() => null);
        if (hub) setHubDisplay(hub);
        const opts = await getShippingOptions(cep, items, {
          recipient_name,
          recipient_phone,
          hubParams,
          order_id: orderId,
        });
        const uiOptions = toSecondPartyOptions(opts, order);
        setShippingOptions(uiOptions);
        const savedSecond = (order.second_party_shipping_option_id ?? "").trim();
        const match = savedSecond ? uiOptions.find((o) => o.id === savedSecond) : undefined;
        setSelectedShippingId(match ? match.id : uiOptions[0]?.id ?? null);
      } catch (e) {
        setShippingOptions([]);
        setSelectedShippingId(null);
        setShippingOptionsError((e as Error)?.message ?? "Não foi possível carregar as opções de envio.");
      } finally {
        setShippingOptionsLoading(false);
      }
    })();
  }, [order, orderId]);

  useEffect(() => {
    if (!order || step !== "shipping" || shippingOptions.length > 0 || autoShippingQuoteOnceRef.current) {
      return;
    }
    autoShippingQuoteOnceRef.current = true;
    loadShippingOptions();
  }, [order, step, shippingOptions.length, loadShippingOptions]);

  const firstLineOfferId = order ? ((order.line_items ?? []) as OrderLineItemApi[])[0]?.offer_id : undefined;
  useEffect(() => {
    if (step !== "review" || !firstLineOfferId) {
      setOfferDetails(null);
      return;
    }
    let cancelled = false;
    setOfferDetailsLoading(true);
    getOfferById(firstLineOfferId)
      .then((offer) => {
        if (cancelled) return;
        const productIds = offer.product_ad_ids ?? [];
        const cashReais = (offer.amount_cents ?? 0) / 100;
        if (productIds.length === 0) {
          setOfferDetails({
            tradeInAds: [],
            cashAmount: cashReais,
            totalOfferValue: cashReais,
          });
          setOfferDetailsLoading(false);
          return;
        }
        return Promise.all(productIds.map((id) => getAdById(id)))
          .then((apiAds) => {
            if (cancelled) return;
            const tradeInAds = apiAds
              .map((api) => {
                const ad = apiAdToAd(api);
                return {
                  id: ad.id,
                  title: ad.title,
                  value: ad.price ?? 0,
                  image: getAdImage(ad) || "",
                };
              })
              .filter((t) => t.id);
            const productsTotal = tradeInAds.reduce((s, t) => s + t.value, 0);
            setOfferDetails({
              tradeInAds,
              cashAmount: cashReais,
              totalOfferValue: cashReais + productsTotal,
            });
          })
          .finally(() => {
            if (!cancelled) setOfferDetailsLoading(false);
          });
      })
      .catch(() => {
        if (!cancelled) {
          setOfferDetails(null);
          setOfferDetailsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [step, firstLineOfferId]);

  // Fetch first line ad (seller's product) for image in Resumo da troca
  const firstLineAdId = order ? ((order.line_items ?? []) as OrderLineItemApi[])[0]?.ad_id : undefined;
  useEffect(() => {
    if (step !== "review" || !firstLineAdId) {
      setOrderProductAd(null);
      return;
    }
    let cancelled = false;
    setOrderProductAdLoading(true);
    getAdById(firstLineAdId)
      .then((api) => {
        if (!cancelled) setOrderProductAd(apiAdToAd(api));
      })
      .catch(() => {
        if (!cancelled) setOrderProductAd(null);
      })
      .finally(() => {
        if (!cancelled) setOrderProductAdLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, firstLineAdId]);

  const selectedOption =
    selectedShippingId != null && selectedShippingId !== ""
      ? shippingOptions.find((o) => o.id === selectedShippingId) ?? null
      : null;
  const selectedAmountCents = selectedOption ? Math.round(selectedOption.price * 100) : null;

  const handleContinueToReview = useCallback(() => {
    setStep("review");
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-primary border-b border-primary sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl text-white">Pagamento da sua parte</h1>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">{error ?? "Pedido não encontrado."}</p>
          <Button onClick={onBack} className="mt-4">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  const lineItems = (order.line_items ?? []) as OrderLineItemApi[];
  const firstLine = lineItems[0];
  const isSeller = isSecondPartySeller(order);

  const handleConcluirPagamento = () => {
    if (!orderId) return;
    setPayLoading(true);
    const shippingCents = selectedAmountCents ?? order.second_party_shipping_cents ?? 0;
    const handlingCents = order.second_party_handling_cents ?? 0;
    const totalCents = shippingCents + handlingCents;
    const optId = (selectedShippingId ?? "").trim();
    const rawCents = isSeller ? selectedOption?.sellerRawPriceCents : selectedOption?.buyerRawPriceCents;
    const markupCents = isSeller ? selectedOption?.sellerFreightMarkupCents : selectedOption?.buyerFreightMarkupCents;
    const piOpts: Parameters<typeof getPaymentIntent>[1] = {};
    if (totalCents > 0) piOpts.amount_cents = totalCents;
    if (optId !== "") {
      piOpts.second_party_shipping_option_id = optId;
      if (shippingCents > 0) piOpts.second_party_shipping_cents = shippingCents;
      if (rawCents != null) piOpts.second_party_shipping_raw_cents = rawCents;
      if (markupCents != null) piOpts.second_party_shipping_freight_markup_cents = markupCents;
    }
    getPaymentIntent(orderId, Object.keys(piOpts).length > 0 ? piOpts : undefined)
      .then((pi) => {
        onPaymentIntentReady(orderId, pi.client_secret, pi.publishable_key ?? "");
      })
      .catch((e) => {
        const msg = (e as Error)?.message ?? "Não foi possível abrir o pagamento.";
        alert(msg);
      })
      .finally(() => {
        setPayLoading(false);
      });
  };

  if (step === "shipping") {
    const routeLabels = buildSecondPartyReviewRouteLabels(order, hubDisplay, isSeller);

    return (
      <CheckoutShippingPage
        onBack={onBack}
        shippingOptions={shippingOptions}
        shippingOptionsLoading={shippingOptionsLoading}
        shippingOptionsError={shippingOptionsError}
        selectedShippingId={selectedShippingId}
        onSelectShipping={setSelectedShippingId}
        onContinue={handleContinueToReview}
        routeLabels={routeLabels}
        shippingPageTitle={`Pedido #${order.order_id}`}
        shippingInfoBanner="order"
        onRetryShippingQuote={() => {
          autoShippingQuoteOnceRef.current = false;
          setShippingOptionsError(null);
          loadShippingOptions();
        }}
      />
    );
  }

  const shippingReais = selectedOption ? selectedOption.price : (order.second_party_shipping_cents ?? 0) / 100;
  const handlingReais = (order.second_party_handling_cents ?? 0) / 100;
  const totalReais = shippingReais + handlingReais;
  const reviewRouteLabels = buildSecondPartyReviewRouteLabels(order, hubDisplay, isSeller);
  const reviewLegs = secondPartyReviewLegsReais(order, selectedOption);

  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setStep("shipping")}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white leading-snug">Pedido #{order.order_id}</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Negotiation summary: desired product / swap item (seller's ad), product = R$ 0 */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Resumo da troca</h3>
          </div>
          <div className="flex items-start gap-3 pl-7">
            {(orderProductAdLoading || orderProductAd) && (
              <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted">
                {orderProductAdLoading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : orderProductAd ? (
                  <AdImage
                    src={getAdImage(orderProductAd) || ""}
                    alt={orderProductAd.title ?? firstLine?.product_name ?? ""}
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">
                {firstLine?.product_name ?? orderProductAd?.title ?? "Pedido de troca"}
              </p>
              <p className="text-sm text-muted-foreground">
                {firstLine?.quantity ? `Quantidade: ${firstLine.quantity}` : "Troca"}
              </p>
              <p className="text-sm font-semibold text-primary mt-1">
                Subtotal: {formatCurrency(0)}
              </p>
            </div>
          </div>
        </div>

        {/* Detalhes da Oferta: same as buyer checkout — produtos oferecidos + pagamento em dinheiro + valor total */}
        {(offerDetailsLoading || offerDetails) && (
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold mb-3 text-foreground">Detalhes da Oferta</h3>
            {offerDetailsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Carregando detalhes...</span>
              </div>
            ) : offerDetails ? (
              <div className="rounded-lg border border-border bg-muted/30 dark:bg-muted/10 p-3 space-y-3 text-sm">
                {offerDetails.tradeInAds.length > 0 && (
                  <>
                    <p className="text-muted-foreground font-medium">Produto(s) oferecido(s):</p>
                    <ul className="space-y-2">
                      {offerDetails.tradeInAds.map((t) => (
                        <li key={t.id} className="flex items-center gap-3 rounded-lg bg-background/50 p-2">
                          <AdImage
                            src={t.image || undefined}
                            alt={t.title}
                            className="w-12 h-12 object-cover rounded shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{t.title}</p>
                            <p className="text-primary font-medium">{formatCurrency(t.value)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Valor dos produtos:</span>
                      <span>{formatCurrency(offerDetails.tradeInAds.reduce((s, t) => s + t.value, 0))}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-muted-foreground">
                  <span>Pagamento em dinheiro:</span>
                  <span>{formatCurrency(offerDetails.cashAmount)}</span>
                </div>
                <div className="flex justify-between font-semibold text-primary pt-1 border-t border-border">
                  <span>Valor total da oferta:</span>
                  <span>{formatCurrency(offerDetails.totalOfferValue)}</span>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {(selectedOption != null || shippingReais > 0) && (
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Forma de Envio</h3>
            </div>
            <div className="pl-7">
              {reviewRouteLabels && (
                <div className="mb-3 rounded-lg border border-border bg-muted/30 dark:bg-muted/10 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Trajeto do envio</p>
                  <p className="text-sm text-foreground">
                    <span className="font-medium">{safeText(reviewRouteLabels.origin)}</span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="font-medium">{safeText(reviewRouteLabels.hub)}</span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="font-medium">{safeText(reviewRouteLabels.destination)}</span>
                  </p>
                  {(reviewLegs.leg1 != null || reviewLegs.leg2 != null) && (
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {reviewLegs.leg1 != null && (
                        <span>Até o hub: {formatCurrency(reviewLegs.leg1)}</span>
                      )}
                      {reviewLegs.leg2 != null && (
                        <span>Hub até destino: {formatCurrency(reviewLegs.leg2)}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {selectedOption != null ? (
                <>
                  <p className="font-medium">{safeText(selectedOption.name)}</p>
                  <p className="text-sm text-muted-foreground">{safeText(selectedOption.estimatedDays)}</p>
                  <p className="text-sm font-semibold text-primary mt-1">
                    {formatCurrency(selectedOption.price)}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium">Frete (sua parte)</p>
                  <p className="text-sm text-muted-foreground">Opção registrada no pedido</p>
                  <p className="text-sm font-semibold text-primary mt-1">{formatCurrency(shippingReais)}</p>
                </>
              )}
            </div>
          </div>
        )}

        <div className="bg-card border-2 border-primary/20 rounded-lg p-6 space-y-3">
          <h2 className="font-semibold text-lg text-primary">Resumo do pagamento</h2>
          <div className="flex justify-between text-base">
            <span className="text-muted-foreground">Subtotal dos produtos</span>
            <span className="font-medium">{formatCurrency(0)}</span>
          </div>
          <div className="flex justify-between text-base">
            <span className="text-muted-foreground">Frete</span>
            <span className="font-medium shrink-0">{formatCurrency(shippingReais)}</span>
          </div>
          {handlingReais > 0 && (
            <div className="flex justify-between text-base">
              <span className="text-muted-foreground">Serviços de Manuseio</span>
              <span className="font-medium">{formatCurrency(handlingReais)}</span>
            </div>
          )}
          <div className="border-t border-border pt-4 flex justify-between items-center">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-2xl font-bold text-primary">{formatCurrency(totalReais)}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground text-center">
          A forma de pagamento será escolhida na próxima tela (Stripe).
        </p>

        <Button
          className="w-full"
          size="lg"
          onClick={handleConcluirPagamento}
          disabled={payLoading || totalReais <= 0}
        >
          {payLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Abrindo pagamento...
            </>
          ) : (
            "Concluir pagamento"
          )}
        </Button>
      </div>
    </div>
  );
}
